import {
    IGuidesManager,
    IHandleDragAdsorbGuidesProps,
    IMultiRect,
    IOperationFrame,
    IOriginalSize,
} from '@src/slide/application/service/guides-manager/index.interface';
import {
    EPpGuideOrientation, EUpdateGuidesType,
    IGuide, IGuideModel, IMasterLayoutGuides,
} from '@src/slide/domain/ui-model/element-modeles/Interface/guide-model.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { observable } from 'mobx';
import SlideGuidesService from '@src/slide/application/service/guides-manager/slide-guides-service';
import MasterGuidesService from '@src/slide/application/service/guides-manager/master-guides-service';
import LayoutGuidesService from '@src/slide/application/service/guides-manager/layout-guides-service';
import {
    IMasterLayoutEditService,
} from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { EDIT_TYPE } from '@src/slide/domain/selection/constant/edit-type';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { ISlideGuidesService } from '@src/slide/application/service/guides-manager/slide-guides-service.interface';
import { IMasterGuidesService } from '@src/slide/application/service/guides-manager/master-guides-service.interface';
import { ILayoutGuidesService } from '@src/slide/application/service/guides-manager/layout-guides-service.interface';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { IGuideModel as IGuideModelV3 } from '@src/slide/domain/ui-model/element-modeles/Interface/guide-model-v3.interface';
import { IPresentationDataModel } from '@tencent/slide-sdk-model';
import { transaction } from '@src/slide/domain/slide-app';
import GuideModel from '@src/slide/domain/ui-model/element-modeles/guide-model-v3';
import { BaseGuideLine } from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/guide-component/components/guide-line';
import { GuideEditType } from '@src/slide/application/service/guides-manager/base-guides-service';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';

enum EDisplayGuidesLocalValue {
    show = 0,
    hide = 1,
}

interface IGuidesManagerProps {
    uiDataService: IUiDataService
    masterLayoutEditService: IMasterLayoutEditService;
    selectionService: ISelectionService;
}

const ABSORB_SIZE = 15;
const LEFT_DRAG_OFFSET = 0;

/**
 * 参考线管理
 * 管理slide、master、layout三个参考线service
 * 管理全局状态
 */
export default class GuidesManager implements IGuidesManager {
    /**
     * 是否展示对齐线
     */
    @observable public displayGuides: boolean;

    /**
     * 当前操作的参考线
     */
    @observable public currentGuideModelMap: {
        guide?: IGuide;
        e?: MouseEvent;
        pageX: number;
        pageY: number;
    } = {
        guide: undefined,
        e: undefined,
        pageX: 0,
        pageY: 0,
    };

    public uiComponentMap: WeakMap<IGuide, BaseGuideLine> = new Map();

    public guideModel: IGuideModel;

    /**
     * 编辑页参考线
     */
    public slideGuidesService: SlideGuidesService;

    /**
     * 母版参考线
     */
    public masterGuidesService: MasterGuidesService;

    /**
     * 板式参考线
     */
    public layoutGuidesService: LayoutGuidesService;

    private readonly uiDataService: IUiDataService;
    private readonly masterLayoutEditService: IMasterLayoutEditService;
    private readonly selectionService: ISelectionService;
    private slideWidth: number;
    private slideHeight: number;

    public constructor(props: IGuidesManagerProps) {
        const {
            uiDataService,
            masterLayoutEditService,
            selectionService,
        } = props;
        // 页面刷新后，参考线状态依然保存
        const displayGuides = userLocalStorage.getItem(LocalStorageKeys.DISPLAY_GUIDES);
        this.displayGuides = displayGuides === EDisplayGuidesLocalValue.show;
        this.uiDataService = uiDataService;
        this.selectionService = selectionService;
        this.slideGuidesService = new SlideGuidesService({
            selectionService: this.selectionService,
        });
        this.masterGuidesService = new MasterGuidesService({
            selectionService: this.selectionService,
        });
        this.layoutGuidesService = new LayoutGuidesService({
            selectionService: this.selectionService,
        });
        this.masterLayoutEditService = masterLayoutEditService;

        eventBus.on(EventKeys.ALL_SLIDE_DATA_CONVERT, () => {
            this.guideModel = this.makeV3GuideData();
            this.initGuides();
        });
    }

    public initGuides = () => {
        this.slideGuidesService.setGuideModel(this.guideModel);
        this.masterGuidesService.setGuideModel(this.guideModel);
        this.layoutGuidesService.setGuideModel(this.guideModel);
        const slideGuides = this.guideModel?.slideGuides;
        const masterGuides = this.guideModel?.masterGuides;
        const layoutGuides = this.guideModel?.layoutGuides;
        this.slideGuidesService.initGuides(slideGuides);
        this.masterGuidesService.initGuides(masterGuides);
        this.layoutGuidesService.initGuides(layoutGuides);
    };

    public setCanvasSize = (props) => {
        const { slideWidth, slideHeight } = props;
        const changeVertRadio = this.slideWidth ? slideWidth / this.slideWidth : 1;
        const changeHorzRadio = this.slideHeight ? slideHeight / this.slideHeight : 1;

        this.slideWidth = slideWidth;
        this.slideHeight = slideHeight;
        this.slideGuidesService.setCanvasSize(props);
        this.masterGuidesService.setCanvasSize(props);
        this.layoutGuidesService.setCanvasSize(props);
        if (changeVertRadio !== 1 || changeHorzRadio !== 1) {
            this.updateGuidesPosWhenCanvasSizeChange(changeVertRadio, changeHorzRadio);
        }
    };

    /**
     * 设置是否展示参考线，并缓存状态
     */
    public changeDisplayGuides = (): void => {
        this.displayGuides = !this.displayGuides;
        if (this.displayGuides) {
            userLocalStorage.setItem(LocalStorageKeys.DISPLAY_GUIDES, EDisplayGuidesLocalValue.show);
            this.initGuidesIfEmptyDataByEditType();
        } else {
            userLocalStorage.setItem(LocalStorageKeys.DISPLAY_GUIDES, EDisplayGuidesLocalValue.hide);
        }
    };

    public initGuidesIfEmptyDataByEditType = () => {
        if (this.masterLayoutEditService?.editType === EDIT_TYPE.SLIDE) {
            this.slideGuidesService.initGuidesIfEmptyData(GuideEditType.slide);
            return;
        }

        if (this.selectionService.currentSlide?.getRelationMasterModel() === this.selectionService.currentSlide) {
            this.masterGuidesService.initGuidesIfEmptyData(GuideEditType.master);
            return;
        }

        this.layoutGuidesService.initGuidesIfEmptyData(GuideEditType.layout);
    };

    /**
     * 设置当前操作的参考线
     */
    public setCurrentGuide = (guideModel?: IGuide): void => {
        this.currentGuideModelMap.guide = guideModel;
    };

    public setDisplayGuides = (displayGuides): void => {
        this.displayGuides = displayGuides;
        if (this.displayGuides) {
            userLocalStorage.setItem(LocalStorageKeys.DISPLAY_GUIDES, EDisplayGuidesLocalValue.show);
        } else {
            userLocalStorage.setItem(LocalStorageKeys.DISPLAY_GUIDES, EDisplayGuidesLocalValue.hide);
        }
    };

    public setCurrentMouseEvent = (props: { e: MouseEvent, pageX: number, pageY: number }): void => {
        const { e, pageX, pageY } = props;
        this.currentGuideModelMap.e = e;
        this.currentGuideModelMap.pageX = pageX;
        this.currentGuideModelMap.pageY = pageY;
    };

    /**
     * 增加垂直对齐线
     * 根据编辑情况，分发给slide/master/layout参考线
     */
    public addVertGuide = (): void => {
        // 隐藏参考线的情况下，新增参考线等同于展示参考线
        if (!this.displayGuides) {
            this.changeDisplayGuides();
            return;
        }
        this.getOperationService()?.addVertGuides({
            currentGuide: this.currentGuideModelMap.guide,
            e: this.currentGuideModelMap.e,
            pageX: this.currentGuideModelMap.pageX,
            pageY: this.currentGuideModelMap.pageY,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.GUIDES,
            action: TDWActionType.ADD_VERTICAL_GUIDE,
        });
    };

    /**
     * 增加水平对齐线
     */
    public addHorzGuide = (): void => {
        // 隐藏参考线的情况下，新增参考线等同于展示参考线
        if (!this.displayGuides) {
            this.changeDisplayGuides();
            return;
        }
        this.getOperationService()?.addHorzGuides({
            currentGuide: this.currentGuideModelMap.guide,
            e: this.currentGuideModelMap.e,
            pageX: this.currentGuideModelMap.pageX,
            pageY: this.currentGuideModelMap.pageY,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.GUIDES,
            action: TDWActionType.ADD_HORIZONTAL_GUIDE,
        });
    };

    /**
     * 删除垂直对齐线
     */
    public deleteGuide = (guideModel: IGuide): void => {
        this.getOperationService()?.deleteGuide(guideModel);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.GUIDES,
            action: TDWActionType.GUIDES_DELETE,
        });
    };

    /**
     * 设置当前操作的参考线的坐标
     */
    public setCurrentGuidePos = (changedPos: number): void => {
        const { guide } = this.currentGuideModelMap;
        if (guide) {
            this.getOperationService()?.setGuidePos(changedPos, guide);
        }
    };

    public setCurrentGuideColor = (color: string): void => {
        const { guide } = this.currentGuideModelMap;
        if (guide) {
            this.getOperationService()?.setGuideColor(color, guide);
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.GUIDES,
            action: TDWActionType.GUIDES_COLOR,
        });
    };

    /**
     * 获取要操作的是slide、master还是layout的service
     */
    public getOperationService(): ISlideGuidesService | IMasterGuidesService | ILayoutGuidesService | undefined {
        if (this.masterLayoutEditService.editType === EDIT_TYPE.SLIDE) {
            return this.slideGuidesService;
        }
        if (this.selectionService.currentSlide === this.selectionService.currentSlide?.getRelationMasterModel()) {
            return this.masterGuidesService;
        }
        if (this.selectionService.currentSlide === this.selectionService.currentSlide?.getRelationLayoutModel()) {
            return this.layoutGuidesService;
        }
    }

    /**
     * 打平slide、master、layout的参考线，分辨计算垂直/水平向吸附
     * todo: 拆个吸附manager
     */
    public handleDragAdsorbGuides = (props: IHandleDragAdsorbGuidesProps): IOperationFrame => {
        const { offsetFrame, originalSize, mode, model, multiRect, sizeMap } = props;
        const {
            vertGuides,
            horzGuides,
        } = this.slideGuidesService;
        const {
            vertGuides: masterVertGuides,
            horzGuides: masterHorzGuides,
        } = this.masterGuidesService;
        const {
            vertGuides: layoutVertGuides,
            horzGuides: layoutHorzGuides,
        } = this.layoutGuidesService;
        const allVertList: Array<IGuide> = [...vertGuides, ...masterVertGuides, ...layoutVertGuides];
        const allHorzList: Array<IGuide> = [...horzGuides, ...masterHorzGuides, ...layoutHorzGuides];

        // 计算垂直吸附
        this.computeOperationFrame({
            allGuidesList: allVertList,
            offsetFrame,
            originalSize,
            mode,
            model,
            multiRect,
            sizeMap,
        });

        // 计算水平吸附
        this.computeOperationFrame({
            allGuidesList: allHorzList,
            offsetFrame,
            originalSize,
            mode,
            model,
            multiRect,
            sizeMap,
        });
        return offsetFrame;
    };

    /**
     * 计算吸附偏移，分发拖拽、缩放的处理
     * @param props
     * @private
     */
    private computeOperationFrame(props: {
        allGuidesList: Array<IGuide>,
        offsetFrame: IOperationFrame,
        originalSize: IOriginalSize,
        mode: string,
        model: IElementBaseModel,
        multiRect?: IMultiRect | null,
        sizeMap: any;
    }) {
        const {
            allGuidesList,
            offsetFrame,
            originalSize,
            mode,
            model,
            multiRect,
            sizeMap,
        } = props;

        switch (mode) {
            case MODES.MOVE:
                this.computeDragOffset({
                    allGuidesList,
                    originalSize,
                    offsetFrame,
                });
                if (multiRect?.length) {
                    this.computeDragOffset({
                        allGuidesList,
                        originalSize: multiRect,
                        offsetFrame,
                    });
                }
                break;
            default:
                this.computeResizeOffset({
                    allGuidesList,
                    originalSize,
                    offsetFrame,
                    model,
                    sizeMap,
                });
        }
    }

    /**
     * 处理缩放吸附，需要额外关注元素反转
     * @private
     */
    private computeResizeOffset(props: {
        allGuidesList: Array<IGuide>,
        originalSize: IOriginalSize,
        offsetFrame: IOperationFrame,
        model: IElementBaseModel,
        sizeMap?: Map<any, any>,
    }) {
        const { allGuidesList, originalSize, model, sizeMap } = props;
        let { offsetFrame } = props;
        if (!allGuidesList?.length) return;
        // 处理多选
        if (sizeMap?.size) {
            offsetFrame = sizeMap.get(model.key)?.offsetFrame;
        }

        // 处理组合
        let parentLeft = 0; let parentTop = 0;
        if (model && model.parent?.type === UIModelType.GROUP) {
            parentLeft = (model.parent as IGroupModel).props.style.left || 0;
            parentTop = (model.parent as IGroupModel).props.style.top || 0;
        }

        let minAbsorb = Number.MAX_VALUE;
        let originStart; let offsetStart;
        let originalLength; let offsetKeyName;
        let offsetLength; let styleLengthKeyName;
        const flipH = model?.props?.style?.flipH;
        const flipV = model?.props?.style?.flipV;
        const { orient } = allGuidesList[0];
        const isVert = orient === EPpGuideOrientation.ppVerticalGuide;
        if (isVert) {
            offsetKeyName = 'left';
            styleLengthKeyName = 'width';
            originStart = originalSize.left + parentLeft;
            offsetStart = offsetFrame.left;
            originalLength = originalSize.width;
            offsetLength = offsetFrame.width;
        } else {
            offsetKeyName = 'top';
            styleLengthKeyName = 'height';
            originStart = originalSize.top + parentTop;
            offsetStart = offsetFrame.top;
            originalLength = originalSize.height;
            offsetLength = offsetFrame.height;
        }

        // eslint-disable-next-line complexity
        allGuidesList.forEach((guide) => {
            // 判断是否是反转场景
            const isRevert = ((!!offsetFrame.flipH !== !!flipH) && isVert)
                || ((!!flipV !== !!offsetFrame.flipV) && !isVert);

            if (isRevert) {
                const topDiff = Math.abs(originStart + originalLength + offsetStart + offsetLength - guide.pos);
                // 吸附右/下边框
                if (offsetFrame[offsetKeyName] > 0) {
                    if (topDiff < ABSORB_SIZE && topDiff <= minAbsorb) {
                        offsetFrame[styleLengthKeyName] = guide.pos - originStart - originalLength - originalLength;
                        minAbsorb = topDiff;
                    }
                } else {
                    // 吸附左/上边框
                    const topDiff = Math.abs(originStart + offsetStart - guide.pos);
                    if (topDiff < ABSORB_SIZE && topDiff <= minAbsorb) {
                        const diff = guide.pos - (originStart + offsetFrame[offsetKeyName]);
                        offsetFrame[offsetKeyName] = guide.pos - originStart;
                        offsetFrame[styleLengthKeyName] = offsetFrame[styleLengthKeyName] - diff;
                        minAbsorb = topDiff;
                    }
                }
            } else {
                // 吸附右/下边框
                const topDiff = Math.abs(originStart + originalLength + offsetLength - guide.pos);
                if (Math.floor(Math.abs(offsetFrame[offsetKeyName])) === 0) {
                    if (topDiff < ABSORB_SIZE && topDiff < minAbsorb) {
                        offsetFrame[styleLengthKeyName] = guide.pos - originStart - originalLength;
                        minAbsorb = topDiff;
                    }
                } else {
                    // 吸附左/上边框
                    const topDiff = Math.abs(originStart + offsetStart - guide.pos);
                    if (topDiff < ABSORB_SIZE && topDiff < minAbsorb) {
                        offsetFrame[offsetKeyName] = guide.pos - originStart;
                        offsetFrame[styleLengthKeyName] = -offsetFrame[offsetKeyName];
                        minAbsorb = topDiff;
                    }
                }
            }
        });
    }

    /**
     * 计算拖拽吸附
     * 拖拽需要处理多选
     * @private
     */
    private computeDragOffset(props: {
        allGuidesList: Array<IGuide>,
        originalSize: IOriginalSize,
        offsetFrame: IOperationFrame,
    }) {
        const { allGuidesList, originalSize, offsetFrame } = props;
        if (!allGuidesList?.length) return;
        let minAbsorb = Number.MAX_VALUE;
        let originStart; let offsetStart; let originalLength; let offsetKeyName;
        const { orient } = allGuidesList[0];
        if (orient === EPpGuideOrientation.ppVerticalGuide) {
            offsetKeyName = 'left';
            originStart = originalSize.left;
            offsetStart = offsetFrame.left;
            originalLength = originalSize.width;
        } else {
            offsetKeyName = 'top';
            originStart = originalSize.top;
            offsetStart = offsetFrame.top;
            originalLength = originalSize.height;
        }
        allGuidesList.forEach((guide) => {
            // 计算起始侧测吸附
            const topDiff = Math.abs(originStart + offsetStart - guide.pos);
            if (topDiff < ABSORB_SIZE && topDiff <= minAbsorb) {
                offsetFrame[offsetKeyName] = guide.pos - originStart + LEFT_DRAG_OFFSET;
                minAbsorb = topDiff;
            }

            // 计算终点侧吸附
            const bottomDiff = Math.abs(originStart + offsetStart + originalLength - guide.pos);
            if (bottomDiff < ABSORB_SIZE && bottomDiff <= minAbsorb) {
                offsetFrame[offsetKeyName] = guide.pos - (originStart + originalLength) + LEFT_DRAG_OFFSET;
                minAbsorb = bottomDiff;
            }
        });
    }

    /**
     * 当画布尺寸变动时，所有参考线都需要等比缩放
     * @private
     */
    private updateGuidesPosWhenCanvasSizeChange(changeVertRadio, changeHorzRadio) {
        this.slideGuidesService.updateGuidesPosWhenCanvasSizeChange(changeVertRadio, changeHorzRadio);
        this.masterGuidesService.updateGuidesPosWhenCanvasSizeChange(changeVertRadio, changeHorzRadio);
        this.layoutGuidesService.updateGuidesPosWhenCanvasSizeChange(changeVertRadio, changeHorzRadio);
        const props = {
            slideGuides: this.slideGuidesService.getUpdateProps(),
            masterGuides: this.masterGuidesService.getUpdateProps(),
            layoutGuides: this.layoutGuidesService.getUpdateProps(),
        };
        this.guideModel?.updateGuidesPropsNoUndo(props, EUpdateGuidesType.all);
    }

    /**
     * 将3.0的参考线数据结构整合成2.0时的聚合模式
     * @private
     */
    private makeV3GuideData(): IGuideModel {
        const masterGuides: IMasterLayoutGuides[] = this.uiDataService?.presentation?.masterList?.map(e => ({
            id: e.id || '',
            guides: (e.guideModel as IGuideModelV3)?.slideGuideList,
        }));
        const layoutGuides: IMasterLayoutGuides[] = this.uiDataService?.presentation?.layoutList?.map(e => ({
            id: e.id || '',
            guides: (e.guideModel as IGuideModelV3)?.slideGuideList,
        }));
        return {
            slideGuides: (this.uiDataService?.presentation?.guideModel as IGuideModelV3).slideGuideList,
            masterGuides,
            layoutGuides,
            updateGuidesPropsNoUndo: (v2GuidModel: IGuideModel, updateGuidesType: EUpdateGuidesType) => {
                transaction(() => {
                    const presentationSdkData = this.uiDataService?.presentation?.sdkData as IPresentationDataModel;
                    if (!presentationSdkData) {
                        return;
                    }
                    if ([EUpdateGuidesType.all, EUpdateGuidesType.slide].includes(updateGuidesType)) {
                        const guideList = GuideModel.convertDataUiToSdk(v2GuidModel.slideGuides);
                        presentationSdkData.api.updateSlideGuideList(guideList);
                    }
                    if ([EUpdateGuidesType.all, EUpdateGuidesType.master].includes(updateGuidesType)) {
                        v2GuidModel.masterGuides.forEach((e) => {
                            const master = presentationSdkData.masterList.find(master => master.id === e.id);
                            const guideList = GuideModel.convertDataUiToSdk(e.guides);
                            master?.api?.updateSlideGuideList(guideList);
                        });
                    }
                    if ([EUpdateGuidesType.all, EUpdateGuidesType.layout].includes(updateGuidesType)) {
                        v2GuidModel.layoutGuides.forEach((e) => {
                            const layout = presentationSdkData.layoutList.find(layout => layout.id === e.id);
                            const guideList = GuideModel.convertDataUiToSdk(e.guides);
                            layout?.api?.updateSlideGuideList(guideList);
                        });
                    }
                });
            },
        };
    }
}
